import { useState } from "react";
import { toast } from "sonner";
import { HeartHandshake, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import LegalNotice from "@/components/LegalNotice";
import FormConsent from "@/components/FormConsent";

const CARE_TYPES = [
  "Personal care", "Companion care", "Meal preparation", "Medication reminders",
  "Mobility support", "Transportation", "Respite care", "Alzheimer's / Dementia care",
  "Post-hospital recovery", "Other",
];

const SCHEDULES = ["A few hours / week", "Daily (part-time)", "Daily (full-time)", "Overnight", "Live-in", "24/7", "Not sure yet"];

export default function RequestCare() {
  const [form, setForm] = useState({
    client_name: "", phone: "", email: "", care_type: "", schedule: "",
    address: "", special_conditions: "", message: "",
  });
  const [termsOk, setTermsOk] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/care-requests", form);
      toast.success("Care request submitted!");
      setSubmitted(true);
      track("care_request_submitted", { care_type: form.care_type, schedule: form.schedule });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="section" data-testid="request-care-success">
        <div className="container-x max-w-2xl soft-card text-center">
          <CheckCircle2 className="w-14 h-14 text-teal-600 mx-auto" />
          <h1 className="mt-6 text-3xl font-semibold text-slate-900">Your request is in.</h1>
          <p className="mt-4 text-slate-600">A care coordinator will reach out within 24 hours — often much sooner. If you need urgent support, please call <a className="text-brand-navy font-semibold underline" href="tel:+14027611531">(402) 761-1531</a>.</p>
        </div>
      </section>
    );
  }

  return (
    <div data-testid="request-care-page">
      <section className="section bg-gradient-to-b from-slate-50 to-white">
        <div className="container-x max-w-3xl">
          <div className="eyebrow">Request care</div>
          <h1 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
            Tell us about your loved one.
          </h1>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Share a few details and we'll plan the right care together. Everything submitted is kept strictly confidential.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x max-w-3xl">
          <div className="mb-5 grid gap-3">
            <LegalNotice variant="emergency" testId="rc-emergency-notice">
              Melo Hopeful Care does not provide emergency medical services. If you are experiencing chest pain,
              difficulty breathing, severe injury, stroke symptoms, or any life-threatening emergency,
              <strong> call 911 immediately.</strong>
            </LegalNotice>
            <LegalNotice variant="hipaa" testId="rc-hipaa-notice">
              Please avoid including sensitive medical details in this form. Share those securely once a care
              coordinator reaches out.
            </LegalNotice>
          </div>
          <form onSubmit={submit} className="soft-card space-y-6" data-testid="request-care-form">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="label-field">Client full name *</label>
                <input required value={form.client_name} onChange={upd("client_name")} className="input-field" data-testid="rc-client-name" placeholder="Jane Doe" />
              </div>
              <div>
                <label className="label-field">Phone *</label>
                <input required type="tel" value={form.phone} onChange={upd("phone")} className="input-field" data-testid="rc-phone" placeholder="(402) 761-1531" />
              </div>
              <div className="md:col-span-2">
                <label className="label-field">Email *</label>
                <input required type="email" value={form.email} onChange={upd("email")} className="input-field" data-testid="rc-email" placeholder="name@example.com" />
              </div>
              <div>
                <label className="label-field">Care type needed *</label>
                <select required value={form.care_type} onChange={upd("care_type")} className="input-field" data-testid="rc-care-type">
                  <option value="">Select care type</option>
                  {CARE_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label-field">Schedule *</label>
                <select required value={form.schedule} onChange={upd("schedule")} className="input-field" data-testid="rc-schedule">
                  <option value="">Select schedule</option>
                  {SCHEDULES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label-field">Address / service location *</label>
                <input required value={form.address} onChange={upd("address")} className="input-field" data-testid="rc-address" placeholder="Street, City, MO" />
              </div>
              <div className="md:col-span-2">
                <label className="label-field">Special conditions (dementia, mobility, diet, etc.)</label>
                <input value={form.special_conditions} onChange={upd("special_conditions")} className="input-field" data-testid="rc-conditions" placeholder="Anything we should know" />
              </div>
              <div className="md:col-span-2">
                <label className="label-field">Additional message</label>
                <textarea rows={5} value={form.message} onChange={upd("message")} className="input-field" data-testid="rc-message" placeholder="Tell us about your loved one — what they enjoy, what's hard right now, and what great care would look like." />
              </div>
            </div>

            <FormConsent type="care" checked={termsOk} onChange={(e) => setTermsOk(e.target.checked)} testId="rc-consent" />

            <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-4">
              <button disabled={loading} className="btn-primary" data-testid="rc-submit">
                <HeartHandshake className="w-4 h-4" /> {loading ? "Sending…" : "Submit care request"}
              </button>
              <p className="text-xs text-slate-500">Urgent? Call <a className="font-semibold text-brand-navy" href="tel:+14027611531">(402) 761-1531</a> — available 24/7.</p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
